import { useState } from 'react';

import { Api } from '../../api/api';
import type { AccountProfile, Authentication } from '../../api/models';
import { TokenStore } from '../../api/tokenStore';

import type { SignInState } from './SignInState';
import { canSubmit } from './SignInState';

/**
 * Mirrors android/…/Screens/SignIn/SignInViewModel.kt and ios/…/Screens/SignIn/SignInModel.swift.
 *
 * A hook rather than a class — React has no other idiom, and it is divergence 15 in the parity register.
 * It returns the same `SignInState` the other two clients render.
 */
export function useSignIn(onSignedIn: (account: AccountProfile) => void, initialEmail: string) {
  const [state, setState] = useState<SignInState>({
    email: initialEmail,
    password: '',
    isSubmitting: false,
    error: null,
  });

  return {
    state,

    onEmailChange: (email: string): void => setState((current) => ({ ...current, email, error: null })),

    onPasswordChange: (password: string): void =>
      setState((current) => ({ ...current, password, error: null })),

    submit: async (): Promise<void> => {
      if (!canSubmit(state)) return;

      setState((current) => ({ ...current, isSubmitting: true, error: null }));

      const result = await Api.signIn(state.email.trim(), state.password);

      if (result.ok) {
        const authentication = result.value as Authentication;

        TokenStore.writeParentTokens(authentication.tokens.accessToken, authentication.tokens.refreshToken);
        setState((current) => ({ ...current, isSubmitting: false }));
        onSignedIn(authentication.account);

        return;
      }

      // The API's own message is shown. Since R0033 that really is catalogue copy in the caller's
      // language — substituting our own would put back the vocabulary risk the catalogue removes (BR-13).
      setState((current) => ({ ...current, isSubmitting: false, error: result.error.message }));
    },
  };
}
